import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Search, UserPlus, Check, X, Loader2, User, Users, Upload, Download, FileSpreadsheet, CheckCircle, AlertCircle, Eye, ArrowUp, ArrowDown, ArrowUpDown, Phone, RefreshCw } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helpers for click-to-sort on table headers
const nextSortDir = (current) => (current === 'asc' ? 'desc' : current === 'desc' ? null : 'asc');
const SortHead = ({ label, sortKey, sort, onSort, testid }) => {
    const active = sort.key === sortKey;
    const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
    return (
        <button
            type="button"
            onClick={() => onSort(sortKey)}
            className={`inline-flex items-center gap-1 hover:text-[#064E3B] transition-colors ${active ? 'text-[#064E3B] font-semibold' : ''}`}
            data-testid={testid}
        >
            {label} <Icon className="h-3 w-3" />
        </button>
    );
};

const applySort = (rows, sort, keyMap = {}) => {
    if (!sort.key || !sort.dir) return rows;
    const accessor = keyMap[sort.key] || ((r) => r[sort.key]);
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
        const va = accessor(a);
        const vb = accessor(b);
        // Null/undefined go last regardless of direction
        if (va == null && vb == null) return 0;
        if (va == null) return 1;
        if (vb == null) return -1;
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
        return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
};

const WaitingList = ({ mode = 'waiting' }) => {
    const { user } = useAuth();
    const isBranchManager = user?.role === 'branch_manager';
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = location.pathname.startsWith('/manager') ? '/manager' : '/admin';
    const statusFilter = mode === 'pending' ? 'pending_enrolment' : 'waiting_list';
    const pageTitle = mode === 'pending' ? 'Pending List' : 'Waiting List';
    const pageSubtitle = mode === 'pending'
        ? 'Approved students awaiting the €100 registration fee. Paying moves them to Active; you can also move them to Unregistered.'
        : 'Approve, deny or enroll waiting-list students.';

    const [tab, setTab] = useState('students');
    const [students, setStudents] = useState([]);
    const [guardians, setGuardians] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    // Approve / Reject
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [approveDialog, setApproveDialog] = useState(false);
    const [rejectDialog, setRejectDialog] = useState(false);
    const [selectedProgramId, setSelectedProgramId] = useState('');
    const [selectedClassId, setSelectedClassId] = useState('');
    const [rejectReason, setRejectReason] = useState('');
    const [processing, setProcessing] = useState(false);

    // Upload
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadMode, setUploadMode] = useState('students'); // 'students' | 'guardians'
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const fileRef = useRef(null);

    // Click-to-sort state (per tab)
    const [studentSort, setStudentSort] = useState({ key: null, dir: null });
    const [guardianSort, setGuardianSort] = useState({ key: null, dir: null });
    // Branch / program filters (both tabs)
    const [branchFilter, setBranchFilter] = useState('__all__');
    const [programFilter, setProgramFilter] = useState('__all__');
    const branches = React.useMemo(() => Array.from(new Set(programs.map(p => p.location).filter(Boolean))).sort(), [programs]);
    const programById = React.useMemo(() => Object.fromEntries((programs || []).map(p => [p.id, p])), [programs]);
    const cycleStudentSort = (key) => setStudentSort(prev => {
        if (prev.key !== key) return { key, dir: 'asc' };
        const dir = nextSortDir(prev.dir);
        return dir ? { key, dir } : { key: null, dir: null };
    });
    const cycleGuardianSort = (key) => setGuardianSort(prev => {
        if (prev.key !== key) return { key, dir: 'asc' };
        const dir = nextSortDir(prev.dir);
        return dir ? { key, dir } : { key: null, dir: null };
    });

    const uploadEndpoint = uploadMode === 'guardians'
        ? `${API_URL}/api/parents/upload`
        : `${API_URL}/api/students/upload/waiting-list`;
    const templateEndpoint = uploadMode === 'guardians'
        ? `${API_URL}/api/parents/upload/template`
        : `${API_URL}/api/students/upload/waiting-list/template`;
    const templateFileName = uploadMode === 'guardians' ? 'guardians_template.xlsx' : 'waiting_list_template.xlsx';
    const uploadTitle = uploadMode === 'guardians' ? 'Upload Guardians' : 'Upload Waiting-List Students';
    const uploadColumnsHelp = uploadMode === 'guardians'
        ? { required: 'first_name, last_name, email, mobile', optional: 'guardian_code, address, account_type, guardian2_first_name, guardian2_last_name, guardian2_email, guardian2_mobile' }
        : { required: 'first_name, last_name, guardian_code, program_name', optional: 'arabic_name, date_of_birth, gender, address' };

    const openUploadDialog = (mode) => { setUploadMode(mode); setUploadResult(null); setUploadDialogOpen(true); };

    // Log-a-call dialog
    const [callDialog, setCallDialog] = useState(null); // { guardian, mobile }
    const [callNotes, setCallNotes] = useState('');
    const [callSaving, setCallSaving] = useState(false);

    const openCallDialog = (guardian, mobile) => {
        setCallDialog({ guardian, mobile: mobile || '' });
        setCallNotes('');
    };
    const saveCall = async () => {
        if (!callDialog?.guardian) return;
        setCallSaving(true);
        try {
            await axios.post(`${API_URL}/api/calls`, {
                parent_id: callDialog.guardian.id,
                mobile_called: callDialog.mobile,
                notes: callNotes,
            }, { withCredentials: true });
            toast.success('Call logged');
            setCallDialog(null); setCallNotes('');
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to log call');
        } finally { setCallSaving(false); }
    };

    // Resend €100 registration link (Pending mode only)
    const resendPayLink = async (student) => {
        if (!window.confirm(`Send a fresh €100 payment link to ${student.parent?.first_name || 'the guardian'}?`)) return;
        try {
            await axios.post(`${API_URL}/api/waiting-list/resend-registration-link/${student.id}`, {}, { withCredentials: true });
            toast.success('Fresh payment link emailed to guardian');
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send fresh link');
        }
    };

    useEffect(() => { fetchData(); }, [mode]);

    const fetchData = async () => {
        try {
            const [studentsRes, guardiansRes, programsRes, classesRes] = await Promise.all([
                axios.get(`${API_URL}/api/waiting-list?status=${statusFilter}`, { withCredentials: true }),
                axios.get(`${API_URL}/api/waiting-list/guardians`, { withCredentials: true }),
                axios.get(`${API_URL}/api/programs`, { withCredentials: true }),
                axios.get(`${API_URL}/api/classes`, { withCredentials: true })
            ]);
            // Guardians endpoint returns guardians for waiting_list only — for
            // pending mode we filter guardians locally to those with pending kids.
            let guardiansData = guardiansRes.data;
            if (mode === 'pending') {
                const pendingParentIds = new Set(studentsRes.data.map(s => s.parent_id));
                guardiansData = (guardiansRes.data || []).filter(g => pendingParentIds.has(g.id));
                // Also include guardians of pending kids that were missing (e.g. already-active parents)
                const missing = studentsRes.data.map(s => s.parent).filter(Boolean).filter(p => p && !guardiansData.find(g => g.id === p.id));
                guardiansData = [...guardiansData, ...missing];
            }
            setStudents(studentsRes.data);
            setGuardians(guardiansData);
            setPrograms(programsRes.data);
            setClasses(classesRes.data);
        } catch (error) {
            toast.error(`Failed to load ${pageTitle.toLowerCase()}`);
        } finally { setLoading(false); }
    };

    const openApprove = (student) => {
        setSelectedStudent(student);
        setSelectedProgramId(student.program_id || '');
        setSelectedClassId('');
        setApproveDialog(true);
    };

    const handleApprove = async () => {
        setProcessing(true);
        try {
            // Update program if changed
            if (selectedProgramId && selectedProgramId !== selectedStudent.program_id) {
                await axios.put(`${API_URL}/api/students/${selectedStudent.id}`, { program_id: selectedProgramId }, { withCredentials: true });
            }
            const res = await axios.post(`${API_URL}/api/waiting-list/approve`, {
                student_id: selectedStudent.id,
                class_id: selectedClassId || null,
            }, { withCredentials: true });
            toast.success(`Enrolment link (€${res.data.fee}) emailed to guardian`);
            setApproveDialog(false); setSelectedStudent(null); setSelectedClassId(''); setSelectedProgramId('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to approve');
        } finally { setProcessing(false); }
    };

    const handleReject = async () => {
        setProcessing(true);
        try {
            await axios.post(`${API_URL}/api/waiting-list/reject`, { student_id: selectedStudent.id, reason: rejectReason }, { withCredentials: true });
            toast.success('Application rejected');
            setRejectDialog(false); setSelectedStudent(null); setRejectReason('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reject');
        } finally { setProcessing(false); }
    };

    const getProgram = (id) => programs.find(p => p.id === id);
    const getProgramClasses = (programId) => classes.filter(c => c.program_id === programId);

    const guardianById = React.useMemo(() => Object.fromEntries((guardians || []).map(g => [g.id, g])), [guardians]);
    const matchesBranchProgram = (s) => {
        if (programFilter !== '__all__' && s.program_id !== programFilter) return false;
        if (branchFilter !== '__all__') {
            const prog = programById[s.program_id];
            if (!prog || (prog.location || '') !== branchFilter) return false;
        }
        return true;
    };
    const filteredStudents = students.filter(s => {
        if (!matchesBranchProgram(s)) return false;
        const q = searchQuery.trim().toLowerCase();
        if (!q) return true;
        const digits = q.replace(/[^0-9+]/g, '');
        const p = guardianById[s.parent_id] || {};
        const mob1 = (p.mobile || '').replace(/[^0-9+]/g, '');
        const mob2 = (p.guardian2_mobile || '').replace(/[^0-9+]/g, '');
        return (
            (s.first_name || '').toLowerCase().includes(q)
            || (s.last_name || '').toLowerCase().includes(q)
            || (s.arabic_name || '').toLowerCase().includes(q)
            || (s.reg_code || '').toLowerCase().includes(q)
            || (s.date_of_birth || '').toLowerCase().includes(q)
            || (p.first_name || '').toLowerCase().includes(q)
            || (p.last_name || '').toLowerCase().includes(q)
            || (p.email || '').toLowerCase().includes(q)
            || (p.reg_code || '').toLowerCase().includes(q)
            || (digits && (mob1.includes(digits) || mob2.includes(digits)))
        );
    });
    // Guardians filtered by branch/program via THEIR children's programs
    const filteredGuardians = guardians.filter(g => {
        if (branchFilter === '__all__' && programFilter === '__all__') return true;
        const kids = students.filter(s => s.parent_id === g.id);
        return kids.some(matchesBranchProgram);
    });
    // Apply search on top of branch/program-filtered guardians
    const searchFilteredGuardians = filteredGuardians.filter(g => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return true;
        const digits = q.replace(/[^0-9+]/g, '');
        const mob1 = (g.mobile || '').replace(/[^0-9+]/g, '');
        const mob2 = (g.guardian2_mobile || '').replace(/[^0-9+]/g, '');
        return (
            (g.first_name || '').toLowerCase().includes(q) ||
            (g.last_name || '').toLowerCase().includes(q) ||
            (g.reg_code || '').toLowerCase().includes(q) ||
            (g.email || '').toLowerCase().includes(q) ||
            (digits && (mob1.includes(digits) || mob2.includes(digits)))
        );
    });

    // Apply sort AFTER filter so the visible rows are what's ordered
    const sortedStudents = applySort(filteredStudents, studentSort, {
        student_name: s => `${s.last_name || ''} ${s.first_name || ''}`.trim(),
        parent_name: s => `${s.parent?.last_name || ''} ${s.parent?.first_name || ''}`.trim(),
        program: s => getProgram(s.program_id)?.name || '',
        date_of_birth: s => s.date_of_birth || '',
        created_at: s => s.created_at || '',
        reg_code: s => s.reg_code || '',
    });
    const sortedGuardians = applySort(searchFilteredGuardians, guardianSort, {
        reg_code: g => g.reg_code || '',
        guardian_name: g => `${g.last_name || ''} ${g.first_name || ''}`.trim(),
        email: g => g.email || '',
        mobile: g => g.mobile || '',
        students_count: g => g.students_count || 0,
        last_call_at: g => g.last_call_at || '',
    });

    const formatCallDate = (iso) => {
        if (!iso) return null;
        try {
            const d = new Date(iso);
            const pad = (n) => n.toString().padStart(2, '0');
            return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
        } catch { return null; }
    };

    if (loading) {
        return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div></div></DashboardLayout>;
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="waiting-list-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">{pageTitle}</h1>
                        <p className="text-sm text-[#4B5563]">{pageSubtitle}</p>
                        <p className="text-[#4B5563]">Review and approve student applications</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button variant="outline" onClick={() => openUploadDialog('guardians')} data-testid="upload-wl-guardians-btn"><Upload className="h-4 w-4 mr-2" />Upload Guardians</Button>
                        <Button variant="outline" onClick={() => openUploadDialog('students')} data-testid="upload-wl-students-btn"><Upload className="h-4 w-4 mr-2" />Upload Students</Button>
                        <Badge variant="secondary" className="w-fit" data-testid="wl-count-badge"><UserPlus className="h-4 w-4 mr-1" />{sortedGuardians.length} guardians · {sortedStudents.length} students</Badge>
                    </div>
                </div>

                <Card className="card-surface">
                    <CardContent className="p-4">
                        <div className={`grid grid-cols-1 gap-3 ${isBranchManager ? 'md:grid-cols-1' : 'md:grid-cols-3'}`}>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" />
                                <Input placeholder="Search by student name, DOB, guardian name, mobile, email or reg code…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="waiting-list-search" />
                            </div>
                            {!isBranchManager && (
                                <>
                                    <Select value={programFilter} onValueChange={setProgramFilter}>
                                        <SelectTrigger data-testid="wl-program-filter"><SelectValue placeholder="All programs" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__all__">All programs</SelectItem>
                                            {programs.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                    <Select value={branchFilter} onValueChange={setBranchFilter}>
                                        <SelectTrigger data-testid="wl-branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__all__">All branches</SelectItem>
                                            {branches.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </>
                            )}
                        </div>
                    </CardContent>
                </Card>

                <Tabs value={tab} onValueChange={setTab} className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="guardians" data-testid="tab-wl-guardians"><Users className="h-4 w-4 mr-2" />Guardians ({sortedGuardians.length})</TabsTrigger>
                        <TabsTrigger value="students" data-testid="tab-wl-students"><User className="h-4 w-4 mr-2" />Students ({sortedStudents.length})</TabsTrigger>
                    </TabsList>

                    <TabsContent value="guardians">
                        <Card className="card-surface"><CardContent className="p-0">
                            {sortedGuardians.length > 0 ? (
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead><SortHead label="Code" sortKey="reg_code" sort={guardianSort} onSort={cycleGuardianSort} testid="sort-wl-g-reg_code" /></TableHead>
                                        <TableHead><SortHead label="Guardian" sortKey="guardian_name" sort={guardianSort} onSort={cycleGuardianSort} testid="sort-wl-g-name" /></TableHead>
                                        <TableHead><SortHead label="Email" sortKey="email" sort={guardianSort} onSort={cycleGuardianSort} testid="sort-wl-g-email" /></TableHead>
                                        <TableHead>Mobiles</TableHead>
                                        <TableHead><SortHead label="Last Call" sortKey="last_call_at" sort={guardianSort} onSort={cycleGuardianSort} testid="sort-wl-g-last-call" /></TableHead>
                                        <TableHead><SortHead label="Students on WL" sortKey="students_count" sort={guardianSort} onSort={cycleGuardianSort} testid="sort-wl-g-count" /></TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {sortedGuardians.map(g => (
                                            <TableRow key={g.id} className="cursor-pointer hover:bg-[#FAF9F6]" data-testid={`wl-guardian-row-${g.id}`}>
                                                <TableCell onClick={() => navigate(`${basePath}/parents/${g.id}`)}><Badge variant="outline" className="font-mono tabular-nums">{g.reg_code}</Badge></TableCell>
                                                <TableCell className="font-medium" onClick={() => navigate(`${basePath}/parents/${g.id}`)}>{g.first_name} {g.last_name}</TableCell>
                                                <TableCell className="text-sm" onClick={() => navigate(`${basePath}/parents/${g.id}`)}>{g.email}</TableCell>
                                                <TableCell className="text-sm">
                                                    <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                                                        {g.mobile && (
                                                            <button
                                                                type="button"
                                                                className="inline-flex items-center gap-1 text-[#064E3B] hover:underline text-left"
                                                                onClick={() => openCallDialog(g, g.mobile)}
                                                                data-testid={`wl-g1-mobile-${g.id}`}
                                                            ><Phone className="h-3 w-3" />{g.mobile}<span className="text-[10px] text-[#6B7280]">G1</span></button>
                                                        )}
                                                        {g.guardian2_mobile && (
                                                            <button
                                                                type="button"
                                                                className="inline-flex items-center gap-1 text-[#7C3AED] hover:underline text-left"
                                                                onClick={() => openCallDialog(g, g.guardian2_mobile)}
                                                                data-testid={`wl-g2-mobile-${g.id}`}
                                                            ><Phone className="h-3 w-3" />{g.guardian2_mobile}<span className="text-[10px] text-[#6B7280]">G2</span></button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-xs tabular-nums" onClick={() => navigate(`${basePath}/parents/${g.id}`)} data-testid={`wl-last-call-${g.id}`}>
                                                    {formatCallDate(g.last_call_at) || <span className="text-[#9CA3AF] italic">Never</span>}
                                                </TableCell>
                                                <TableCell onClick={() => navigate(`${basePath}/parents/${g.id}`)}><div className="flex items-center gap-2"><Badge className="bg-[#F59E0B]">{g.students_count}</Badge><span className="text-xs text-[#4B5563] line-clamp-1">{(g.student_names || []).join(', ')}</span></div></TableCell>
                                                <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`${basePath}/parents/${g.id}`); }}><Eye className="h-4 w-4" /></Button></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : <div className="flex flex-col items-center justify-center py-12"><Users className="h-12 w-12 text-[#4B5563]/30 mb-4" /><p className="text-[#4B5563]">No guardians with pending students</p></div>}
                        </CardContent></Card>
                    </TabsContent>

                    <TabsContent value="students">
                        <Card className="card-surface"><CardContent className="p-0">
                            {sortedStudents.length > 0 ? (
                                <Table>
                                    <TableHeader><TableRow>
                                        <TableHead><SortHead label="Student" sortKey="student_name" sort={studentSort} onSort={cycleStudentSort} testid="sort-wl-s-name" /></TableHead>
                                        <TableHead><SortHead label="Parent" sortKey="parent_name" sort={studentSort} onSort={cycleStudentSort} testid="sort-wl-s-parent" /></TableHead>
                                        <TableHead><SortHead label="Desired Program" sortKey="program" sort={studentSort} onSort={cycleStudentSort} testid="sort-wl-s-program" /></TableHead>
                                        <TableHead><SortHead label="Date of Birth" sortKey="date_of_birth" sort={studentSort} onSort={cycleStudentSort} testid="sort-wl-s-dob" /></TableHead>
                                        <TableHead><SortHead label="Registration Date" sortKey="created_at" sort={studentSort} onSort={cycleStudentSort} testid="sort-wl-s-created" /></TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow></TableHeader>
                                    <TableBody>
                                        {sortedStudents.map(student => (
                                            <TableRow key={student.id} className="cursor-pointer hover:bg-[#FAF9F6]" onClick={() => openApprove(student)} data-testid={`wl-student-row-${student.id}`}>
                                                <TableCell>
                                                    <div className="flex items-center space-x-3">
                                                        <div className="h-10 w-10 rounded-full bg-[#064E3B]/10 flex items-center justify-center"><User className="h-5 w-5 text-[#064E3B]" /></div>
                                                        <div>
                                                            <p className="font-medium text-[#1F2937]">{student.first_name} {student.last_name}</p>
                                                            <p className="text-xs text-[#4B5563] tabular-nums">{student.reg_code}</p>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>{student.parent && <div><p className="text-sm">{student.parent.first_name} {student.parent.last_name}</p><p className="text-xs text-[#4B5563]">{student.parent.email}</p></div>}</TableCell>
                                                <TableCell><Badge variant="outline">{getProgram(student.program_id)?.name || 'N/A'}</Badge></TableCell>
                                                <TableCell className="tabular-nums">{student.date_of_birth}</TableCell>
                                                <TableCell className="tabular-nums">{student.created_at ? new Date(student.created_at).toLocaleDateString() : '—'}</TableCell>
                                                <TableCell className="text-right"><div className="flex items-center justify-end space-x-2" onClick={(e) => e.stopPropagation()}>
                                                    {mode === 'pending' ? (
                                                        <>
                                                            <Button
                                                                size="sm" variant="outline"
                                                                className="text-[#064E3B] border-[#10B981]/40 hover:bg-[#10B981]/10"
                                                                onClick={() => resendPayLink(student)}
                                                                data-testid={`pending-resend-link-${student.id}`}
                                                            ><RefreshCw className="h-4 w-4 mr-1" />Resend Pay Link</Button>
                                                            <Button
                                                                size="sm" variant="outline"
                                                                className="text-[#B45309] border-[#F59E0B]/40 hover:bg-[#F59E0B]/10"
                                                                onClick={async () => {
                                                                    if (!window.confirm('Move this student to the Unregistered list?')) return;
                                                                    try {
                                                                        await axios.put(`${API_URL}/api/students/${student.id}/status`, { status: 'unregistered' }, { withCredentials: true });
                                                                        toast.success('Moved to Unregistered');
                                                                        fetchData();
                                                                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                                                                }}
                                                                data-testid={`pending-unregister-${student.id}`}
                                                            ><X className="h-4 w-4 mr-1" />Unregister</Button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Button size="sm" variant="outline" className="text-[#10B981] border-[#10B981]/40 hover:bg-[#10B981]/10" onClick={() => openApprove(student)} data-testid={`approve-${student.id}`}><Check className="h-4 w-4 mr-1" />Enrol (€100)</Button>
                                                            <Button size="sm" variant="outline" className="text-[#EF4444] hover:bg-[#EF4444]/10" onClick={() => { setSelectedStudent(student); setRejectDialog(true); }} data-testid={`reject-${student.id}`}><X className="h-4 w-4" /></Button>
                                                        </>
                                                    )}
                                                </div></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : <div className="flex flex-col items-center justify-center py-12"><UserPlus className="h-12 w-12 text-[#4B5563]/30 mb-4" /><p className="text-[#4B5563]">No pending applications</p></div>}
                        </CardContent></Card>
                    </TabsContent>
                </Tabs>

                {/* Approve Dialog */}
                <Dialog open={approveDialog} onOpenChange={setApproveDialog}>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-['Outfit']">Enrol Student — Send €100 Enrolment Link</DialogTitle></DialogHeader>
                        <div className="space-y-4 py-4">
                            {selectedStudent && (
                                <div className="p-4 bg-[#FAF9F6] rounded-lg space-y-1">
                                    <p className="font-medium">{selectedStudent.first_name} {selectedStudent.last_name}</p>
                                    <p className="text-xs text-[#4B5563]">Reg code: {selectedStudent.reg_code} · DOB: {selectedStudent.date_of_birth}</p>
                                </div>
                            )}
                            <div className="p-3 bg-[#FFF7ED] border border-[#FED7AA] rounded-lg text-sm text-[#9A3412]">
                                Enrolling this student will <strong>email the guardian a Stripe payment link for €100 (enrolment fee — same as renewal)</strong>.
                                The student becomes <strong>active</strong> only after the fee is paid.
                                Class assignment is optional — you can leave it blank and assign a class later.
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Program</label>
                                <Select value={selectedProgramId} onValueChange={(v) => { setSelectedProgramId(v); setSelectedClassId(''); }}>
                                    <SelectTrigger data-testid="program-select"><SelectValue placeholder="Select a program" /></SelectTrigger>
                                    <SelectContent>
                                        {programs.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Assign to Class <span className="text-xs text-[#6B7280] font-normal">(optional)</span></label>
                                <Select value={selectedClassId} onValueChange={setSelectedClassId} disabled={!selectedProgramId}>
                                    <SelectTrigger data-testid="class-select"><SelectValue placeholder={selectedProgramId ? "Select a class — or leave blank" : "Pick a program first"} /></SelectTrigger>
                                    <SelectContent>
                                        {getProgramClasses(selectedProgramId).map(cls => (<SelectItem key={cls.id} value={cls.id}>{cls.arabic_name || cls.english_name || cls.name} {cls.room ? `(${cls.room})` : ''}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setApproveDialog(false)}>Cancel</Button>
                            <Button onClick={handleApprove} disabled={processing} className="bg-[#064E3B] hover:bg-[#047857] text-white" data-testid="confirm-approve">{processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Enrol & Send Link</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Reject Dialog */}
                <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-['Outfit']">Reject Application</DialogTitle></DialogHeader>
                        <div className="space-y-4 py-4">
                            {selectedStudent && (<div className="p-4 bg-[#FAF9F6] rounded-lg"><p className="font-medium">{selectedStudent.first_name} {selectedStudent.last_name}</p></div>)}
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Reason (optional)</label>
                                <Input placeholder="Enter rejection reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setRejectDialog(false)}>Cancel</Button>
                            <Button onClick={handleReject} disabled={processing} variant="destructive" data-testid="confirm-reject">{processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Reject</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Log a Call */}
                <Dialog open={!!callDialog} onOpenChange={(o) => !o && setCallDialog(null)}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="font-['Outfit'] flex items-center gap-2"><Phone className="h-5 w-5 text-[#064E3B]" />Log a Call</DialogTitle>
                        </DialogHeader>
                        {callDialog && (
                            <div className="space-y-4 py-2">
                                <div className="p-3 bg-[#FAF9F6] rounded-lg text-sm">
                                    <p className="font-medium">{callDialog.guardian.first_name} {callDialog.guardian.last_name}</p>
                                    <p className="text-xs text-[#4B5563]">Calling: <span className="font-mono">{callDialog.mobile}</span></p>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-medium">Notes</label>
                                    <Input placeholder="What did you discuss?" value={callNotes} onChange={(e) => setCallNotes(e.target.value)} data-testid="wl-call-notes" />
                                </div>
                            </div>
                        )}
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setCallDialog(null)}>Cancel</Button>
                            <Button className="bg-[#064E3B] hover:bg-[#047857] text-white" onClick={saveCall} disabled={callSaving} data-testid="wl-call-save">
                                {callSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Phone className="h-4 w-4 mr-2" />}Save Call
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Upload */}
                <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                    <DialogContent className="max-w-md">
                        <DialogHeader><DialogTitle className="font-['Outfit'] flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-[#064E3B]" />{uploadTitle}</DialogTitle></DialogHeader>
                        <div className="space-y-4">
                            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-4 text-sm text-[#166534]"><p className="font-medium mb-1">Required columns:</p><p>{uploadColumnsHelp.required}</p><p className="mt-1 text-xs">Optional: {uploadColumnsHelp.optional}</p></div>
                            <Button variant="outline" className="w-full" data-testid="download-wl-template-btn" onClick={async () => { try { const r = await axios.get(templateEndpoint, { withCredentials: true, responseType: 'blob' }); const u = window.URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = u; a.setAttribute('download', templateFileName); document.body.appendChild(a); a.click(); a.remove(); } catch { toast.error('Failed'); } }}><Download className="h-4 w-4 mr-2" />Download Template</Button>
                            <div className="border-2 border-dashed border-[#D1D5DB] rounded-lg p-6 text-center">
                                <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={async (e) => { const file = e.target.files[0]; if (!file) return; setUploading(true); setUploadResult(null); const fd = new FormData(); fd.append('file', file); try { const r = await axios.post(uploadEndpoint, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }); setUploadResult(r.data); if (r.data.created > 0) { toast.success(`${r.data.created} ${uploadMode === 'guardians' ? 'guardian(s)' : 'student(s)'} added`); fetchData(); } } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; } }} className="hidden" id="wl-upload" data-testid="wl-upload-input" />
                                <label htmlFor="wl-upload" className="cursor-pointer">{uploading ? <div className="flex flex-col items-center"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B] mb-2" /><p className="text-sm">Uploading...</p></div> : <div className="flex flex-col items-center"><Upload className="h-8 w-8 text-[#4B5563] mb-2" /><p className="text-sm font-medium">Click to upload</p></div>}</label>
                            </div>
                            {uploadResult && (<div className="space-y-2"><div className="flex items-center gap-2 text-sm"><CheckCircle className="h-4 w-4 text-[#10B981]" /><span>{uploadResult.created} added</span></div>{uploadResult.skipped > 0 && <div className="flex items-center gap-2 text-sm text-[#F59E0B]"><AlertCircle className="h-4 w-4" /><span>{uploadResult.skipped} skipped</span></div>}{uploadResult.errors?.length > 0 && <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3 max-h-32 overflow-y-auto">{uploadResult.errors.map((er, i) => <p key={`err-${i}-${er.slice(0,30)}`} className="text-xs text-[#DC2626]">{er}</p>)}</div>}</div>)}
                        </div>
                        <DialogFooter><Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Close</Button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default WaitingList;
